import type {Component, Editor} from 'grapesjs';
import type {PrimaryConfigOptions} from '../index';
import type {
    SliderStyleProps,
    SliderChangeParams,
    SliderStyleObject,
    SliderCreateData,
    SliderEmitData,
    SliderUpdateData
} from '../email-maker.types';
import {ExtendedControlsPrefix} from "../vos/extendedcontrolsprefix";
import {ExtendedControlIcons} from "../vos/extendedcontrolicons";

export default (opt: PrimaryConfigOptions): void => {
    const editor: Editor = opt.editor;
    const styleManager = editor.StyleManager;
    const SPACING_PROPERTIES: string[] = [
        'margin',
        'margin-left',
        'margin-right',
        'margin-top',
        'margin-bottom',
        'padding',
        'padding-left',
        'padding-right',
        'padding-top',
        'padding-bottom'
    ];
    const BORDER_RADIUS = 'border-radius';
    const PROP_LOCK: Record<string, boolean> = {};
    const PREV_VALUES: Record<string, number> = {};

    const SLIDER_MORE_OPTIONS_ICON_1 = 1;
    const SLIDER_MORE_OPTIONS_ICON_2 = 2;
    const SLIDER_MORE_OPTIONS_ICON_3 = 3;
    const SLIDER_MORE_OPTIONS_ICON_4 = 4;


    styleManager.addType(window.PMW.emails.styles.ID_SLIDER, {
        create: (data: any): HTMLElement => {
            const {props, change} = data;
            const el = document.createElement('div');
            props.component = props.component ?? editor.getSelected();

            if (typeof props.step === 'undefined') {
                props.step = 1;
            }

            if (typeof props.min === 'undefined') {
                props.min = 0;
            }

            if (typeof props.inputType === 'undefined') {
                props.inputType = 'text';
            }

            if (typeof props.max === 'undefined') {
                props.max = 100;
            }

            if (typeof props.units === 'undefined') {
                props.units = '';
            }

            if (typeof props.extendedControls === 'undefined') {
                props.extendedControls = false;
            }

            if (props.property === 'border-radius') {
                const componentEl = props.component?.getEl()?.getBoundingClientRect() ?? {width: 0, height: 0};
                const max = Math.min(componentEl.width, componentEl.height) / 2;
                props.max = max ?? props.max;
            }

            el.innerHTML = getSliderHTML(props);
            const desktopEl = el.querySelector('.js-container-desktop') as HTMLElement;
            const tabletEl = el.querySelector('.js-container-tablet') as HTMLElement;
            const mobileEl = el.querySelector('.js-container-mobile') as HTMLElement;

            function attachHandlers(containerEl: HTMLElement): void {
                const inputEl = containerEl.querySelector('.pmw-slider') as HTMLInputElement;
                if (inputEl) {
                    sliderControlsHandlers(inputEl, props, change, containerEl);
                    if (props.extendedControls) {
                        sliderExtendedControlsHandlers(containerEl, props, change);
                    }
                    setSliderInitialValue(containerEl, props);
                }
            }

            const defaultPropName: string = props.property;
            if (desktopEl) {
                attachHandlers(desktopEl);
            }

            if (props.isResponsive) {
                const propertyName: string = props.property;
                props.property = window.PMW.emails.MOBILE_CSS_PROP_PREFIX + propertyName;
                if (mobileEl) {
                    attachHandlers(mobileEl);
                }

                props.property = window.PMW.emails.TABLET_CSS_PROP_PREFIX + propertyName;
                if (tabletEl) {
                    attachHandlers(tabletEl);
                }
            }

            props.property = defaultPropName;

            return el;
        },
        emit: (data: any, {el, event, complete, propertyName, isSingleProperty}: any): void => {
            const {props} = data;
            let property: string = props.property;

            if (typeof propertyName === 'string') {
                property = propertyName;
            }

            if (typeof property === 'undefined') {
                return;
            }

            const value: string = (event.target as HTMLInputElement).value;
            const tabletCssPropRegex = new RegExp(`^${window.PMW.emails.TABLET_CSS_PROP_PREFIX}`);
            const mobileCssPropRegex = new RegExp(`^${window.PMW.emails.MOBILE_CSS_PROP_PREFIX}`);
            const isTabletPropUpdate = props.isResponsive && tabletCssPropRegex.test(property);
            const isMobilePropUpdate = props.isResponsive && mobileCssPropRegex.test(property);

            handleCustomProperty(property, value);
            if (isTabletPropUpdate && props.component) {
                editor.runCommand(window.PMW.emails.commands.SET_DEVICE_MEDIA_QUERY, {
                    property,
                    value: value + (props.units ?? ''),
                    id: props.component.ccid,
                    deviceID: window.PMW.emails.DEVICE_TABLET,
                    cssPrefix: window.PMW.emails.TABLET_CSS_PROP_PREFIX,
                });

            } else if (isMobilePropUpdate && props.component) {
                editor.runCommand(window.PMW.emails.commands.SET_DEVICE_MEDIA_QUERY, {
                    property,
                    value: value + (props.units ?? ''),
                    id: props.component.ccid,
                    deviceID: window.PMW.emails.DEVICE_MOBILE,
                    cssPrefix: window.PMW.emails.MOBILE_CSS_PROP_PREFIX,
                });
            }

            toggleVisibility(props.toggleProp, value);

            let units = 'px';
            if (typeof props.units !== 'undefined') {
                units = props.units;
            }

            const valueRes: string = value + units;
            if (props.component) {
                editor.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {property, value: valueRes, component: props.component});

                if (property === 'width' && props.component?.get('tagName').toLowerCase() === 'img') {
                    editor.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {
                        property: 'height',
                        value: 'auto',
                        component: props.component
                    });
                }
            }

            toggleSliderControlsDisabledStateIfExists(el, props.max ?? 100, props.min ?? 0, value);
            gaTrackingForHeight(property, props.component, valueRes);

            if (props.extendedControls && !isSingleProperty) {
                const extendedControlsHelper = getExtendedControlsPrefixes(property);
                const topProperty = extendedControlsHelper.getPrefixedTopProperty();
                const bottomProperty = extendedControlsHelper.getPrefixedBottomProperty();
                const leftProperty = extendedControlsHelper.getPrefixedLeftProperty();
                const rightProperty = extendedControlsHelper.getPrefixedRightProperty();

                gaTrackingForSpacing(property, props.component);

                if (property !== 'margin' && props.component) {
                    editor.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {property: leftProperty, value: valueRes, component: props.component});
                    editor.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {property: rightProperty, value: valueRes, component: props.component});
                }

                if (props.component) {
                    editor.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {property: topProperty, value: valueRes, component: props.component});
                    editor.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {property: bottomProperty, value: valueRes, component: props.component});
                    editor.trigger('component:styleUpdate:' + property, props.component);
                }
            } else {
                if (props.extendedControls && isSingleProperty) {
                    updateSliderInputTextIfNeeded(el);
                }
                gaTrackingForCustomSpacing(property, props.component);
            }
        },
        update: (data: any): void => {
            // This function is intentionally empty as updates are handled elsewhere
        }
    });

    function getExtendedControlsPrefixes(property: string): ExtendedControlsPrefix {
        let topPrefix = '-top';
        let bottomPrefix = '-bottom';
        let leftPrefix = '-left';
        let rightPrefix = '-right';

        if (isBorderRadiusProperty(property)) {
            topPrefix = '-top-right-radius';
            bottomPrefix = '-bottom-right-radius';
            leftPrefix = '-bottom-left-radius';
            rightPrefix = '-top-left-radius';
        }

        return new ExtendedControlsPrefix(property, topPrefix, rightPrefix, bottomPrefix, leftPrefix);
    }

    function toggleSliderControlsDisabledStateIfExists(sliderEl: HTMLElement, maxValue: number, minValue: number, currentValue: string): void {
        const sliderControlMinus = sliderEl.querySelector('.pmw-slider-minus') as HTMLElement;
        const sliderControlPlus = sliderEl.querySelector('.pmw-slider-plus') as HTMLElement;

        if (!sliderControlMinus || !sliderControlPlus) {
            return;
        }

        if (Number(currentValue) === Number(maxValue)) {
            sliderControlPlus.classList.add('disabled');
            sliderControlMinus.classList.remove('disabled');
        } else if (Number(currentValue) === Number(minValue)) {
            sliderControlMinus.classList.add('disabled');
            sliderControlPlus.classList.remove('disabled');
        } else {
            sliderControlPlus.classList.remove('disabled');
            sliderControlMinus.classList.remove('disabled');
        }
    }

    function gaTrackingForHeight(_property: string, _component: Component | undefined, _eventValue: string): void {
        if (_property === 'height') {
            if (isPropLockUnavailable(_property)) {
                return;
            } else {
                setPropLock(_property);
            }

            // Tracking temporarily disabled on 8th Nov 2023
            // editor.runCommand(window.PMW.emails.commands.TRACK_GA_EVENT_COMPONENT, {
            //     component: _component,
            //     eventAction: 'EmailMaker_UpdateHeight',
            //     eventValue: _eventValue
            // });

            freePropLockInInterval(_property);
        }
    }

    function gaTrackingForSpacing(_property: string, _component: Component | undefined): void {
        if (SPACING_PROPERTIES.includes(_property)) {
            if (isPropLockUnavailable(_property)) {
                return;
            } else {
                setPropLock(_property);
            }

            // Tracking temporarily disabled on 8th Nov 2023
            // editor.runCommand(window.PMW.emails.commands.TRACK_GA_EVENT_COMPONENT, {
            //     component: _component,
            //     eventAction: 'EmailMaker_UpdateSpacing'
            // });

            freePropLockInInterval(_property);
        }
    }

    function updateSliderInputTextIfNeeded(el: HTMLElement): void {
        const topElement = el.querySelector('.extended-property-input-top') as HTMLInputElement;
        const bottomElement = el.querySelector('.extended-property-input-bottom') as HTMLInputElement;
        const leftElement = el.querySelector('.extended-property-input-left') as HTMLInputElement;
        const rightElement = el.querySelector('.extended-property-input-right') as HTMLInputElement;

        const topValue = topElement?.value;
        const bottomValue = bottomElement?.value;
        const leftValue = leftElement?.value;
        const rightValue = rightElement?.value;

        const values = [topValue, bottomValue, leftValue, rightValue];
        const inputElement = el.querySelector('.pmw-slider-text') as HTMLInputElement;
        const inputUnits = el.querySelector('.pmw-email-editor-slider-units') as HTMLElement;

        if (!inputElement || !inputUnits) {
            return;
        }

        const isMixedInput = new Set(values).size !== 1;

        if (isMixedInput) {
            inputElement.type = 'text';
            inputElement.value = window.i18next.t('pmwjs_mixed');
            inputElement.classList.add('-mixed');
            inputUnits.classList.add('_hidden');
        } else {
            inputElement.type = 'number';
            inputElement.value = topValue ?? '';
            inputElement.classList.remove('-mixed');
            inputUnits.classList.remove('_hidden');
        }
    }

    function gaTrackingForCustomSpacing(_property: string, _component: Component | undefined): void {
        if (SPACING_PROPERTIES.includes(_property)) {
            if (isPropLockUnavailable(_property)) {
                return;
            } else {
                setPropLock(_property);
            }

            // Tracking temporarily disabled on 8th Nov 2023
            // editor.runCommand(window.PMW.emails.commands.TRACK_GA_EVENT_COMPONENT, {
            //     component: _component,
            //     eventAction: 'EmailMaker_CustomSpacing',
            //     eventValue: _property
            // });

            freePropLockInInterval(_property);
        }
    }

    /**
     * Using a lock to make sure we don't bombard analytics with slider events
     */
    function isPropLockUnavailable(property: string): boolean {
        if (PROP_LOCK[property] === undefined) {
            PROP_LOCK[property] = false;
            return false;
        }

        return PROP_LOCK[property];
    }

    function setPropLock(property: string): void {
        PROP_LOCK[property] = true;
    }

    function freePropLockInInterval(property: string, interval = 2000): void {
        setTimeout(() => PROP_LOCK[property] = false, interval);
    }

    function sliderExtendedControlsHandlers(el: HTMLElement, props: SliderStyleProps, change: (...args: any[]) => void): void {
        const sliderMoreOptions = el.querySelector('.pmw-slider-more-options') as HTMLElement;
        if (sliderMoreOptions) {
            sliderMoreOptions.addEventListener('click', function (this: HTMLElement): void {
                const extendedControlsParent = el.querySelector('.extended-properties-container') as HTMLElement;
                const currentComponent = props.component;
                
                if (extendedControlsParent) {
                    extendedControlsParent.classList.toggle('_hidden');

                    if (extendedControlsParent.classList.contains('_hidden')) {
                        currentComponent?.addAttributes({'data-has-extended-controls': 0});
                        this.classList.remove('-selected');
                    } else {
                        currentComponent?.addAttributes({'data-has-extended-controls': 1});
                        this.classList.add('-selected');
                    }
                }
            });
        }

        const extendedPropertyInputs = el.querySelectorAll('.extended-property-input') as NodeListOf<HTMLInputElement>;
        const extendedPropertyInputContainers = el.querySelectorAll('.extended-property-container') as NodeListOf<HTMLElement>;

        extendedPropertyInputs.forEach((input: HTMLInputElement) => {
            const extendedPropertyContainer = input.closest('.extended-property-container') as HTMLElement;
            const containerId = extendedPropertyContainer?.getAttribute('data-id');
            const moreOptionsFirstIcon = el.querySelector('.more-options-icon') as HTMLElement;
            const moreOptionsSelectedIcon = el.querySelector(`.more-options-icon[data-id="${containerId}"]`) as HTMLElement;

            input.addEventListener('focus', function (): void {
                extendedPropertyInputContainers.forEach((container: HTMLElement) => {
                    container.classList.remove('-selected');
                });

                if (extendedPropertyContainer) {
                    extendedPropertyContainer.classList.add('-selected');
                }
                if (moreOptionsFirstIcon) {
                    moreOptionsFirstIcon.classList.add('-mixed');
                }
                if (moreOptionsSelectedIcon) {
                    moreOptionsSelectedIcon.classList.remove('_hidden');
                }
            });

            input.addEventListener('blur', function (): void {
                if (extendedPropertyContainer) {
                    extendedPropertyContainer.classList.remove('-selected');
                }
                if (moreOptionsSelectedIcon) {
                    moreOptionsSelectedIcon.classList.add('_hidden');
                }
                if (moreOptionsFirstIcon) {
                    moreOptionsFirstIcon.classList.remove('-mixed');
                }
            });
        });

        const extendedControlsHelper = getExtendedControlsPrefixes(props.property);

        if (props.property !== 'margin') {
            const rightInput = el.querySelector('.extended-property-input-right') as HTMLInputElement;
            if (rightInput) {
                rightInput.addEventListener('change', function (event: Event): void {
                    const propertyName = extendedControlsHelper.getPrefixedRightProperty();
                    const isSingleProperty = true;
                    change({el, event, propertyName, isSingleProperty});
                });
            }

            const leftInput = el.querySelector('.extended-property-input-left') as HTMLInputElement;
            if (leftInput) {
                leftInput.addEventListener('change', function (event: Event): void {
                    const propertyName = extendedControlsHelper.getPrefixedLeftProperty();
                    const isSingleProperty = true;
                    change({el, event, propertyName, isSingleProperty});
                });
            }
        }

        const topInput = el.querySelector('.extended-property-input-top') as HTMLInputElement;
        if (topInput) {
            topInput.addEventListener('change', function (event: Event): void {
                const propertyName = extendedControlsHelper.getPrefixedTopProperty();
                const isSingleProperty = true;
                change({el, event, propertyName, isSingleProperty});
            });
        }

        const bottomInput = el.querySelector('.extended-property-input-bottom') as HTMLInputElement;
        if (bottomInput) {
            bottomInput.addEventListener('change', function (event: Event): void {
                const propertyName = extendedControlsHelper.getPrefixedBottomProperty();
                const isSingleProperty = true;
                change({el, event, propertyName, isSingleProperty});
            });
        }
    }

    function sliderControlsHandlers(inputEl: HTMLInputElement, props: SliderStyleProps, change: (...args: any[]) => void, el: HTMLElement): void {
        const propertyName: string = props.property;

        inputEl.addEventListener('change', function (event: Event): void {
            const newValue: string = (el.querySelector('.pmw-slider') as HTMLInputElement)?.value ?? '';

            // setting a temporary value so the actual value change can trigger save & undo
            editor.getModel().skip((_: any) => { onSliderInput(el, event, props, change, PREV_VALUES[propertyName]); });

            onSliderInput(el, event, props, change, newValue);
            PREV_VALUES[propertyName] = parseFloat(newValue);
        });

        inputEl.addEventListener('input', function (event: Event): void {
            editor.getModel().skip((_: any) => {
                onSliderInput(el, event, props, change);
            });
        });

        inputEl.addEventListener('mouseup', function (event: Event): void {
            editor.getModel().skip((_: any) => {
                onSliderInput(el, event, props, change);
            });
        });

        const sliderText = el.querySelector('.pmw-slider-text') as HTMLInputElement;
        const sliderTextContainer = sliderText?.closest('.pmw-slider-input-container') as HTMLElement;
        const sliderControlsContainer = sliderText?.closest('.pmw-slider-step-controls-container') as HTMLElement;

        if (sliderTextContainer && sliderControlsContainer && sliderText) {
            sliderTextContainer.addEventListener('click', function (): void {
                sliderControlsContainer.classList.add('-selected');
                sliderText.focus();
            });

            sliderText.addEventListener('focus', function (): void {
                sliderControlsContainer.classList.add('-selected');

                if (sliderText.type === 'text') {
                    sliderText.type = 'number';
                    sliderText.value = '';
                    sliderText.classList.remove('-mixed');
                }

                const unitContainer = sliderTextContainer.querySelector('.pmw-email-editor-slider-units') as HTMLElement;
                if (unitContainer) {
                    unitContainer.classList.remove('_hidden');
                }
            });

            sliderText.addEventListener('blur', function (): void {
                sliderControlsContainer.classList.remove('-selected');
            });

            sliderText.addEventListener('change', function (event: Event): void {
                const isSingleProperty = false;
                const slider = el.querySelector('.pmw-slider') as HTMLInputElement;
                const textbox = el.querySelector('.pmw-slider-text') as HTMLInputElement;
                
                if (slider && textbox) {
                    const val = getValidatedPropertyValue(textbox);

                    slider.value = val.toString();
                    if (props.extendedControls) {
                        updateExtendedControlInputs(el, val);
                    }

                    editor.runCommand(window.PMW.emails.commands.UPDATE_SLIDER_VALUE, {inputElements: [slider, textbox], property: propertyName, value: slider.value});
                    change({el, event, propertyName, isSingleProperty});
                }
            });
        }

        const sliderPlus = el.querySelector('.pmw-slider-plus') as HTMLElement;
        const sliderMinus = el.querySelector('.pmw-slider-minus') as HTMLElement;
        
        if (sliderPlus) {
            sliderPlus.addEventListener('click', function (): void {
                const input = el.querySelector('.pmw-slider') as HTMLInputElement;
                if (input) {
                    input.value = (parseFloat(input.value) + (props.step ?? 1)).toString();
                    triggerSliderChange(input);
                }
            });
        }

        if (sliderMinus) {
            sliderMinus.addEventListener('click', function (): void {
                const input = el.querySelector('.pmw-slider') as HTMLInputElement;
                if (input) {
                    input.value = (parseFloat(input.value) - (props.step ?? 1)).toString();
                    triggerSliderChange(input);
                }
            });
        }
    }

    /**
     * Handles slider input events
     */
    function onSliderInput(el: HTMLElement, event: Event, props: SliderStyleProps, change: (...args: any[]) => void, value?: string | number): void {
        const isSingleProperty = false;
        let propertyName: string = props.property;
        const slider = el.querySelector('.pmw-slider') as HTMLInputElement;
        const textbox = el.querySelector('.pmw-slider-text') as HTMLInputElement;

        if (!slider || !textbox) {
            return;
        }

        const finalValue: string = typeof value !== "undefined" ? value.toString() : slider.value;
        slider.value = finalValue;
        textbox.value = finalValue;

        if (props.extendedControls) {
            updateExtendedControlInputs(el, parseFloat(finalValue));
        }

        if (props.isResponsive) {
            const selectedDevice = editor.Devices.getSelected();
            if (selectedDevice?.id === window.PMW.emails.DEVICE_MOBILE) {
                propertyName = window.PMW.emails.MOBILE_CSS_PROP_PREFIX + props.property;
            }

            if (selectedDevice?.id === window.PMW.emails.DEVICE_TABLET) {
                propertyName = window.PMW.emails.TABLET_CSS_PROP_PREFIX + props.property;
            }
        }

        editor.runCommand(window.PMW.emails.commands.UPDATE_SLIDER_VALUE, {inputElements: [slider, textbox], property: propertyName, value: finalValue});
        change({el, event, propertyName, isSingleProperty});
    }

    function updateExtendedControlInputs(el: HTMLElement, value: number): void {
        const topInput = el.querySelector('.extended-property-input-top') as HTMLInputElement;
        const bottomInput = el.querySelector('.extended-property-input-bottom') as HTMLInputElement;
        const leftInput = el.querySelector('.extended-property-input-left') as HTMLInputElement;
        const rightInput = el.querySelector('.extended-property-input-right') as HTMLInputElement;

        if (topInput) topInput.value = value.toString();
        if (bottomInput) bottomInput.value = value.toString();
        if (leftInput) leftInput.value = value.toString();
        if (rightInput) rightInput.value = value.toString();
    }

    function getValidatedPropertyValue(el: HTMLInputElement): number {
        const value = parseInt(el.value);
        const min = parseInt(el.getAttribute('min') ?? '0');
        const max = parseInt(el.getAttribute('max') ?? '100');

        if (value < min) {
            el.value = min.toString();
            return min;
        } else if (value > max) {
            el.value = max.toString();
            return max;
        }

        return value;
    }

    function isBorderRadiusProperty(style: string): boolean {
        return style === BORDER_RADIUS;
    }

    function getAllStyledProperties(props: SliderStyleProps): SliderStyleObject {
        const styles = props.component?.getStyle();
        let topStyle = 0;
        let bottomStyle = 0;
        let leftStyle = 0;
        let rightStyle = 0;
        const extendedControlsHelper = getExtendedControlsPrefixes(props.property);

        if (styles) {
            if (typeof styles[extendedControlsHelper.getPrefixedTopProperty()] !== 'undefined') {
                topStyle = parseInt(styles[extendedControlsHelper.getPrefixedTopProperty()] as string);
            } else if (typeof styles[props.property] !== 'undefined') {
                topStyle = parseInt(styles[props.property] as string);
            }

            if (typeof styles[extendedControlsHelper.getPrefixedBottomProperty()] !== 'undefined') {
                bottomStyle = parseInt(styles[extendedControlsHelper.getPrefixedBottomProperty()] as string);
            } else if (typeof styles[props.property] !== 'undefined') {
                bottomStyle = parseInt(styles[props.property] as string);
            }

            if (typeof styles[extendedControlsHelper.getPrefixedRightProperty()] !== 'undefined') {
                rightStyle = parseInt(styles[extendedControlsHelper.getPrefixedRightProperty()] as string);
            } else if (typeof styles[props.property] !== 'undefined') {
                rightStyle = parseInt(styles[props.property] as string);
            }

            if (typeof styles[extendedControlsHelper.getPrefixedLeftProperty()] !== 'undefined') {
                leftStyle = parseInt(styles[extendedControlsHelper.getPrefixedLeftProperty()] as string);
            } else if (typeof styles[props.property] !== 'undefined') {
                leftStyle = parseInt(styles[props.property] as string);
            }
        }
        return {topStyle, bottomStyle, rightStyle, leftStyle};
    }

    function setExtendedControlsValues(el: HTMLElement, props: SliderStyleProps): void {
        const styleObj = getAllStyledProperties(props);

        const topInput = el.querySelector('.extended-property-input-top') as HTMLInputElement;
        const bottomInput = el.querySelector('.extended-property-input-bottom') as HTMLInputElement;
        const rightInput = el.querySelector('.extended-property-input-right') as HTMLInputElement;
        const leftInput = el.querySelector('.extended-property-input-left') as HTMLInputElement;

        if (topInput) topInput.value = styleObj.topStyle.toString();
        if (bottomInput) bottomInput.value = styleObj.bottomStyle.toString();
        if (rightInput) rightInput.value = styleObj.rightStyle.toString();
        if (leftInput) leftInput.value = styleObj.leftStyle.toString();
    }

    function getIconsForSliderMoreOptions(property: string, type?: number): string {
        if (property === 'padding') {
            switch (type) {
                case SLIDER_MORE_OPTIONS_ICON_1:
                    return 'icon-spacing-active-top';
                case SLIDER_MORE_OPTIONS_ICON_2:
                    return 'icon-spacing-active-bottom';
                case SLIDER_MORE_OPTIONS_ICON_3:
                    return 'icon-spacing-active-left';
                case SLIDER_MORE_OPTIONS_ICON_4:
                    return 'icon-spacing-active-right';
                default:
                    return 'icon-edge-spacing';
            }
        }

        if (property === 'border-radius') {
            switch (type) {
                case SLIDER_MORE_OPTIONS_ICON_1:
                    return 'icon-corner-active-right';
                case SLIDER_MORE_OPTIONS_ICON_2:
                    return 'icon-corner-active-bottom-right';
                case SLIDER_MORE_OPTIONS_ICON_3:
                    return 'icon-corner-active-bottom-left';
                case SLIDER_MORE_OPTIONS_ICON_4:
                    return 'icon-corner-active-left';
                default:
                    return 'icon-corner';
            }
        }

        return '';
    }

    function getHideSliderInput(property: string): boolean {
        if (property === 'padding') {
            return true;
        }

        if (property === 'border-radius') {
            return true;
        }

        return false;
    }

    function getSliderHTML(props: SliderStyleProps): string {
        const component = props.component;
        let desktopContainer = '';
        let tabletContainer = '';
        let mobileContainer = '';
        let propertyInputSelectors = '';
        let desktopContainerClassName = 'js-container-desktop';
        const tabletContainerClassName = 'hidden-device-desktop hidden-device-mobile js-container-tablet';
        const mobileContainerClassName = 'hidden-device-desktop hidden-device-tablet js-container-mobile';
        const hideSliderInput = getHideSliderInput(props.property);

        desktopContainerClassName = (props.isResponsive ? 'hidden-device-tablet hidden-device-mobile ' : '') + desktopContainerClassName;

        let extendedControlsAlreadyVisible = false;
        if (props.extendedControls && component && getIsExtendedControlsPanelOpen(component)) {
            extendedControlsAlreadyVisible = true;
        }

        function getSliderContainerMarkup(props: SliderStyleProps, responsiveTag = '', className = ''): string {
            return `<div class="pmw-email-editor-slider-container ${className}">
                <div class="pmw-email-editor-slider-label-container">
                    <span class="style-manager-label"> 
                        ${(responsiveTag ? `<span class="highlight-tag body-sm-bold -rounded">${responsiveTag}</span>` : '') + (props.propertyLabel || '')}
                    </span>
                    <div class="pmw-email-editor-slider-input-options ${props.extendedControls ? '-extended' : ''}">
                        <div class="pmw-slider-step-controls-container flexbox ${hideSliderInput ? '' : '-step-enabled'}">
                            <i class="pmw-slider-minus pmw-icon size-icon-16 icon-minus -secondary -square ${hideSliderInput ? '_hidden' : ''}"></i>
                            <div class="pmw-slider-input-container">
                                <input aria-label="${window.i18next.t('pmwjs_property_slider', {property: props.property})}" data-units="${props.units}" class="pmw-slider-text js-emails-${props.property}-input ${!props.units ? '-center-align' : ''}" type="number" min="${props.min}" max="${props.max}" step="${props.step}"/>
                                <span class="pmw-email-editor-slider-units">${props.units}</span>
                            </div>
                        <i class="pmw-slider-plus pmw-icon size-icon-16 icon-plus -secondary -square ${hideSliderInput ? '_hidden' : ''}"></i>
                        </div>

                        ${(props.extendedControls ? `<div class="pmw-slider-more-options _position-relative ${extendedControlsAlreadyVisible ? '-selected' : ''}">
                                                        <i class="more-options-icon _position-absolute ${getIconsForSliderMoreOptions(props.property)}"></i>
                                                        <i data-id="${SLIDER_MORE_OPTIONS_ICON_1}" class="more-options-icon _position-absolute _hidden content-primary ${getIconsForSliderMoreOptions(props.property, SLIDER_MORE_OPTIONS_ICON_1)}"></i>
                                                        <i data-id="${SLIDER_MORE_OPTIONS_ICON_2}" class="more-options-icon _position-absolute _hidden content-primary ${getIconsForSliderMoreOptions(props.property, SLIDER_MORE_OPTIONS_ICON_2)}"></i>
                                                        <i data-id="${SLIDER_MORE_OPTIONS_ICON_3}" class="more-options-icon _position-absolute _hidden content-primary ${getIconsForSliderMoreOptions(props.property, SLIDER_MORE_OPTIONS_ICON_3)}"></i>
                                                        <i data-id="${SLIDER_MORE_OPTIONS_ICON_4}" class="more-options-icon _position-absolute _hidden content-primary ${getIconsForSliderMoreOptions(props.property, SLIDER_MORE_OPTIONS_ICON_4)}"></i>
                                                    </div>` : '')}
                    </div>
                </div>
                <input type="range" class="pmw-slider js-emails-${props.property}-input ${hideSliderInput ? '_hidden' : ''}" min="${props.min}" max="${props.max}" step="${props.step}" aria-label="${props.property}"/>
                ${propertyInputSelectors} 
            </div>`;
        }

        if (props.extendedControls) {
            let extendedControlIcons: ExtendedControlIcons;

            if (typeof props.extendedControlIcons !== 'undefined' && props.extendedControlIcons instanceof ExtendedControlIcons) {
                extendedControlIcons = props.extendedControlIcons;
            } else {
                extendedControlIcons = new ExtendedControlIcons(
                    'icon-edge-spacing-top',
                    'icon-edge-spacing-bottom',
                    'icon-edge-spacing-left',
                    'icon-edge-spacing-right',
                );
            }

            const styleObj = getAllStyledProperties(props);
            const topStyle = styleObj.topStyle;
            const bottomStyle = styleObj.bottomStyle;
            const rightStyle = styleObj.rightStyle;
            const leftStyle = styleObj.leftStyle;

            propertyInputSelectors = '<div class="extended-properties-container suboption ' + (extendedControlsAlreadyVisible ? "" : "_hidden") + '">' +
                '<div class="extended-property-container" data-id="1">' +
                `<div class="extended-property-icon-container first"><i class="icon ${extendedControlIcons.first} extended-property-icon"></i></div>` +
                '<input type="number" aria-label="' + window.i18next.t("pmwjs_top") + '" class="extended-property-input extended-property-input-top" type="number" value="' + topStyle + `" step="${props.step}" min="${props.min}" max="${props.max}"` + '/>' +
                '</div>' +
                '<div class="extended-property-container" data-id="2">' +
                `<div class="extended-property-icon-container"><i class="icon ${extendedControlIcons.second} extended-property-icon"></i></div>` +
                '<input type="number" aria-label="' + window.i18next.t("pmwjs_bottom") + '" class="extended-property-input extended-property-input-bottom" type="number" value="' + bottomStyle + `" step="${props.step}" min="${props.min}" max="${props.max}"` + '/>' +
                '</div>' +
                '<div class="extended-property-container" data-id="3">' +
                `<div class="extended-property-icon-container"><i class="icon ${extendedControlIcons.third} extended-property-icon"></i></div>` +
                '<input type="number" aria-label="' + window.i18next.t("pmwjs_left") + '" class="extended-property-input extended-property-input-left" type="number" value="' + leftStyle + `" step="${props.step}" min="${props.min}" max="${props.max}"` + '/>' +
                '</div>' +
                '<div class="extended-property-container" data-id="4">' +
                `<div class="extended-property-icon-container"><i class="icon ${extendedControlIcons.fourth} extended-property-icon"></i></div>` +
                '<input type="number" aria-label="' + window.i18next.t("pmwjs_right") + '" class="extended-property-input extended-property-input-right" type="number" value="' + rightStyle + `" step="${props.step}" min="${props.min}" max="${props.max}"` + '/>' +
                '</div>' +
                '</div>';
        }
        desktopContainer = getSliderContainerMarkup(props, '', desktopContainerClassName);

        if (props.isResponsive) {
            const propertyName = props.property;
            props.property = window.PMW.emails.MOBILE_CSS_PROP_PREFIX + propertyName;
            mobileContainer = getSliderContainerMarkup(props, 'Mobile', mobileContainerClassName);
            props.property = window.PMW.emails.TABLET_CSS_PROP_PREFIX + propertyName;
            tabletContainer = getSliderContainerMarkup(props, 'Tablet', tabletContainerClassName);
            props.property = propertyName;
        }

        return `<div class="pmw-email-editor-slider-containers">
            ${desktopContainer}
            ${mobileContainer}
            ${tabletContainer}
        </div>`;
    }

    function setSliderInitialValue(el: HTMLElement, props: SliderStyleProps): void {
        const value = opt.editor.runCommand(window.PMW.emails.commands.GET_COMPONENT_STYLE, {component: props.component, property: props.property});
        const slider = el.querySelector('.pmw-slider') as HTMLInputElement;
        let parsedValue = value !== null ? parseInt(value, 10) : parseInt(props.defaults ?? '0', 10);

        parsedValue = isNaN(parsedValue) ? parseInt(props.defaults ?? '0', 10) : parsedValue;
        
        if (slider) {
            slider.value = parsedValue.toString();
            toggleSliderControlsDisabledStateIfExists(el, props.max ?? 100, props.min ?? 0, parsedValue.toString());

            if (!isNaN(parsedValue)) {
                PREV_VALUES[props.property] = parsedValue;
                const sliderText = el.querySelector('.pmw-slider-text') as HTMLInputElement;
                if (sliderText) {
                    sliderText.value = parsedValue.toString();
                }
                editor.runCommand(window.PMW.emails.commands.UPDATE_SLIDER_VALUE, {inputElements: [slider], property: props.property, value: parsedValue});

                if (props.extendedControls) {
                    setExtendedControlsValues(el, props);
                }
            }
        }
    }

    function getIsExtendedControlsPanelOpen(component: Component): boolean {
        return typeof component.getAttributes()['data-has-extended-controls'] !== 'undefined' && parseInt(component.getAttributes()['data-has-extended-controls']) === 1;
    }

    /**
     * Toggles visibility of properties based on current value
     */
    function toggleVisibility(property: string | undefined, currentValue: string): void {
        if (!property) {
            return;
        }
        const numericValue = parseInt(currentValue);
        const visibilityElement = document.querySelector(`.gjs-sm-property__${property}`) as HTMLElement;

        if (visibilityElement) {
            if (numericValue) {
                visibilityElement.classList.remove('-collapsed');
            } else {
                visibilityElement.classList.add('-collapsed');
            }
        }
    }

    function triggerSliderChange(input: HTMLInputElement): void {
        input.dispatchEvent(new Event('change'));
    }

    function handleCustomProperty(property: string, value: string): void {
        if (property === 'pmw-cell-spacing') {
            editor.runCommand(window.PMW.emails.commands.UPDATE_CELLS_SPACING, {sliderVal: value});
        }

        if (property === 'pmw-cell-border-width') {
            editor.runCommand(window.PMW.emails.commands.UPDATE_CELLS_BORDER_WIDTH, {sliderVal: value});
        }

        if (property === 'pmw-cell-border-radius') {
            editor.runCommand(window.PMW.emails.commands.UPDATE_CELLS_BORDER_RADIUS, {sliderVal: value});
        }

        if (property === 'pmw-icon-margin') {
            editor.runCommand(window.PMW.emails.commands.UPDATE_SOCIAL_LINK_SPACING, {gap: value});
        }

        if (property === 'pmw-icon-size') {
            editor.runCommand(window.PMW.emails.commands.UPDATE_SOCIAL_LINK_SIZE, {size: value});
        }

        if (property === 'padding') {
            editor.runCommand(window.PMW.emails.commands.HANDLE_TABLE_PADDING);
        }
    }
}
